import { BulkHierarchyPolygons, FeatureStateIds, GeoBounds, SourceGroup } from 'app/models';

import { BoundsCalculator } from 'utils/helpers/BoundsCalculator';
import { CompressionUtil } from 'utils/helpers/CompressionUtil';
import { getDeepestItemsByCountry } from 'utils/helpers/countrySourceCatalogHelpers';
import { optimizeNativePolygonIdForHashing } from 'utils/helpers/polygonIdHelpers';
import { SpatialIndex } from 'utils/helpers/SpatialIndex';
import { groupIntoSources } from 'utils/helpers/territoryMapUtils';

type GeoPolygon = {
  geoId: number;
  polygonId: string;
  featureStateIds: FeatureStateIds;
};

export class GeoLookups {
  private readonly polygonsById = new Map<GeoPolygon['polygonId'], GeoPolygon>();
  private readonly polygonsByGeoId = new Map<GeoPolygon['geoId'], GeoPolygon>();

  private readonly polygonNameMap = new Map<GeoPolygon['polygonId'], string>();
  private readonly geographyNameMap = new Map<GeoPolygon['geoId'], string>();

  private readonly pointIndexes: ReadonlyArray<SpatialIndex<string>>;
  private readonly bounds: GeoBounds;
  private readonly sourceGroups: SourceGroup[];

  constructor(init: BulkHierarchyPolygons | null) {
    const leafCatalogMap = getDeepestItemsByCountry(init?.inUseCatalogItems ?? []);
    const duplicatePolygonIds = new Set<string>();
    const boundsCalculator = new BoundsCalculator();

    const pointIndexMap = new Map<string, SpatialIndex<string>>(
      [...leafCatalogMap.values()].map((catalogEntry) => [
        catalogEntry.country,
        new SpatialIndex(SpatialIndex.widthFromCount(catalogEntry.polygonCount))
      ])
    );
    const polygonPages = init?.compressedHierarchyPolygonPages.map((page) => CompressionUtil.decompress(page)) ?? [];
    for (const hierarchyPolygons of polygonPages) {
      const rowCount = hierarchyPolygons?.hierarchyId.length ?? 0;
      for (let index = 0; index < rowCount; index += 1) {
        const name = hierarchyPolygons.name[index];
        const hierarchyId = hierarchyPolygons.hierarchyId[index];
        const nativePolygonid = hierarchyPolygons.polygonId[index];
        const polygonId = optimizeNativePolygonIdForHashing(nativePolygonid);
        const center = hierarchyPolygons.center[index];
        const country = hierarchyPolygons.country[index];
        const sourceKey = hierarchyPolygons.sourceKey[index];

        // Disable polygon if a duplicate
        if (this.polygonsById.has(polygonId) || duplicatePolygonIds.has(polygonId)) {
          duplicatePolygonIds.add(polygonId);
          this.polygonsById.delete(polygonId);
          this.polygonsByGeoId.delete(hierarchyId);
          continue;
        }

        const catalogEntry = leafCatalogMap.get(country);
        if (!catalogEntry || catalogEntry.sourceKey !== sourceKey) {
          // Omit non-leafs from data
          continue;
        }

        const geoPolygon: GeoPolygon = {
          geoId: hierarchyId,
          polygonId,
          featureStateIds: { id: nativePolygonid, source: catalogEntry.sourceId, sourceLayer: catalogEntry.sourceId }
        };
        this.polygonsById.set(polygonId, geoPolygon);
        this.polygonsByGeoId.set(hierarchyId, geoPolygon);
        this.polygonNameMap.set(polygonId, name);
        this.geographyNameMap.set(hierarchyId, name);

        const pointIndex = pointIndexMap.get(country);
        const point = {
          lon: center[0],
          lat: center[1],
          value: polygonId
        };
        pointIndex.add(point);
        boundsCalculator.addPoint(point);
      }
    }

    if (duplicatePolygonIds.size > 0) {
      console.warn(`Hierarchy contains ${duplicatePolygonIds.size} duplicate polygons`, duplicatePolygonIds);
    }

    this.pointIndexes = [...pointIndexMap.values()];
    this.bounds = boundsCalculator.getBounds();
    this.sourceGroups = groupIntoSources(leafCatalogMap.values());
  }

  public hasGeo(geoId: GeoPolygon['geoId']): boolean {
    return this.polygonsByGeoId.has(geoId);
  }

  public getPolygonByGeoId(geoId: GeoPolygon['geoId']) {
    return this.polygonsByGeoId.get(geoId);
  }

  public getPolygonById(polygonId: GeoPolygon['polygonId']) {
    return this.polygonsById.get(polygonId);
  }

  public getBounds(): Readonly<GeoBounds> {
    return this.bounds;
  }

  public getSpatialIndexes(): Iterable<SpatialIndex<string>> {
    return this.pointIndexes;
  }

  public getPolygonNameMap(): ReadonlyMap<string, string> {
    return this.polygonNameMap;
  }

  public getGeographyNameMap(): ReadonlyMap<number, string> {
    return this.geographyNameMap;
  }

  public getSourceGroups(): SourceGroup[] {
    return this.sourceGroups;
  }

  public getGeoName(geoId: number): string {
    const name = this.geographyNameMap.get(geoId);
    if (name === undefined) {
      throw new Error(`GeoId ${geoId} not found`);
    }
    return name;
  }

  public getGeoCount() {
    return this.polygonsById.size;
  }
}
